import { PaymentOption } from '@niarab2c/frontend-commons/src/types/hotels';
import { useImmutableFunction, useIsMobile, usePreviousIfEquals } from '@niaratech/niara-react-components';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { Popper, Fade, Paper } from '@mui/material';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { CriteriaForm } from '../../HotelSearch/Criteria/HotelCriteria';
import { IcShoppingCart } from '../../Icons';
import useTheme from '../../theme/useTheme';
import AddonCart, { AddonCartItem } from '../AddonCart';
import { CartTotalProps } from '../HotelCart/CartTotal';
import HotelCart, { type HotelCartProps } from '../HotelCart/HotelCart';
import ToolTip from '../../microComponents/ToolTip';
import EmptyCart from './emptyCart.svg';
import * as S from './styles';
type ShoppingCartDropdownPropsLocal = Pick<HotelCartProps, 'cartItems' | 'cartTotal' | 'paymentOptions' | 'onCreateQuotation'> & {
  // shoppingCartItems?: CartItem[]
  addonCartItems: AddonCartItem[];
  addonsPaymentOptions: PaymentOption[];
  handleClickCheckout?: () => void;
  onGoToCart?: CartTotalProps['onGoToCart'];
  inNavigationComponent?: boolean; //Prop para saber se este componente está sendo chamado pelo Navigation, para alterar a cor da borda e do ícone.
  tooltipMode?: boolean;
  showModifyButton?: boolean;
  setIsOpenCartModal?: (isOpen: boolean) => void;
  onAddMoreRooms?: (criteria: CriteriaForm) => void;
  expectedRoomsCount?: number;
  isForGroups?: boolean;
  hasSearchTooltipOpened?: boolean;
};
export type ShoppingCartDropdownProps = Omit<ShoppingCartDropdownPropsLocal, 'handleClickCheckout'>;
const ShoppingCartDropdown: React.FC<ShoppingCartDropdownPropsLocal> = props => {
  const {
    handleClickCheckout,
    cartItems,
    addonCartItems,
    cartTotal,
    paymentOptions,
    addonsPaymentOptions,
    inNavigationComponent,
    // tooltipMode,
    onGoToCart,
    onCreateQuotation,
    showModifyButton,
    setIsOpenCartModal,
    // onAddMoreRooms,
    expectedRoomsCount,
    isForGroups,
    hasSearchTooltipOpened
  } = props;
  const [onCartSummary, setOnCartSummary] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const cartRef = useRef<HTMLButtonElement>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const colorText = !inNavigationComponent ? theme?.colors?.textColor : theme?.colors?.navbarText;
  const hasShoppingCartItems = useMemo(() => cartItems?.length > 0, [cartItems?.length]);
  const hasAddonShoppingCartItems = useMemo(() => addonCartItems?.length > 0, [addonCartItems?.length]);
  const handleOpenOnCartSummary = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setShowTooltip(false);
    if (!isMobile) {
      setAnchorEl(event.currentTarget);
      setOnCartSummary(oldOnCartSummary => !oldOnCartSummary);
    } else {
      if (setIsOpenCartModal) setIsOpenCartModal(true);
    }
  }, [isMobile, setIsOpenCartModal]);
  const handleClosed = useCallback(() => {
    setIsOpenCartModal(false);
    setOnCartSummary(false);
    setAnchorEl(null);
  }, [setIsOpenCartModal]);
  const onCheckout = useCallback(() => {
    handleClickCheckout();
    handleClosed();
  }, [handleClickCheckout, handleClosed]);
  const onCreateQuotation2 = useImmutableFunction(!onCreateQuotation ? null : () => {
    onCreateQuotation?.();
    handleClosed();
  });
  const prevCartItems = usePreviousIfEquals(cartItems);
  const numberOfItems = (prevCartItems?.reduce((acc, i) => acc + i.quantity, 0) ?? 0) + (addonCartItems?.reduce((acc, i) => acc + 1 /*i.quantity*/, 0) ?? 0);
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (numberOfItems > 0) {
      setShowTooltip(true);
      timerRef.current = setTimeout(() => setShowTooltip(false), 5000);
    } else setShowTooltip(false);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [numberOfItems, cartItems, isMobile]);
  useEffect(() => {
    if (isMobile) setOnCartSummary(false);
  }, [isMobile]);

  // Fechar dropdown apenas quando item é removido
  const prevHasItems = usePreviousIfEquals(hasShoppingCartItems || hasAddonShoppingCartItems);
  useEffect(() => {
    if (onCartSummary && prevHasItems && !hasShoppingCartItems && !hasAddonShoppingCartItems) {
      handleClosed();
    }
  }, [hasShoppingCartItems, hasAddonShoppingCartItems, onCartSummary, handleClosed, prevHasItems]);
  const showTooltipMessage = showTooltip && !hasSearchTooltipOpened && !onCartSummary && numberOfItems > 0;
  const showCartPopper = onCartSummary && !showTooltipMessage;
  return <>
      <ToolTip open={showTooltipMessage} title={<S.TooltipContent>
            <IoCheckmarkCircleOutline size={20} />
            {numberOfItems > 1 ? `${numberOfItems} itens adicionados ao carrinho` : `${numberOfItems} item adicionado ao carrinho`}
          </S.TooltipContent>} placement={'bottom-end'} arrow={true} fontSize={14} overflowy="unset" padding={20} backgroundcolor={theme?.colors?.success} PopperProps={{
      disablePortal: true,
      modifiers: [{
        name: 'flip',
        enabled: true,
        options: {
          fallbackPlacements: ['bottom', 'bottom-start', 'bottom-end']
        }
      }]
    }}>
        <div style={{
        position: 'relative'
      }}>
          <S.Cart onClick={handleOpenOnCartSummary} type="button" size={showModifyButton ? 'medium' : null} ref={cartRef}>
            {numberOfItems > 0 && <S.NumberItems>{numberOfItems}</S.NumberItems>}
            <IcShoppingCart color={colorText} />
          </S.Cart>
        </div>
      </ToolTip>

      <Popper open={showCartPopper} anchorEl={anchorEl} placement={'bottom-end'} transition disablePortal={false} style={{
      zIndex: 1300
    }} modifiers={[{
      name: 'offset',
      options: {
        offset: [0, 8]
      }
    }, {
      name: 'flip',
      enabled: true,
      options: {
        altBoundary: true,
        fallbackPlacements: ['bottom', 'bottom-start', 'bottom-end']
      }
    }, {
      name: 'preventOverflow',
      enabled: true,
      options: {
        altAxis: true,
        padding: 8
      }
    }, {
      name: 'arrow',
      enabled: true,
      options: {
        element: arrowElement,
        padding: 8
      }
    }, {
      name: 'computeStyles',
      options: {
        gpuAcceleration: false,
        adaptive: true
      }
    }]}>
        {({
        TransitionProps
      }) => <Fade {...TransitionProps} timeout={350}>
            <Paper style={{
          position: 'relative',
          overflow: 'visible',
          marginTop: 8
        }}>
              <S.ArrowPopper ref={setArrowElement} data-popper-arrow />
              <ClickAwayListener onClickAway={handleClosed}>
                <S.ContainerCart>
                  {hasShoppingCartItems ? <HotelCart paymentOptions={paymentOptions} cartItems={cartItems} cartTotal={cartTotal} onCheckout={onCheckout} onCreateQuotation={onCreateQuotation2} onGoToCart={onGoToCart} expectedRoomsCount={expectedRoomsCount} isForGroups={isForGroups} numberOfItems={numberOfItems} addonCartItems={addonCartItems} /> : hasAddonShoppingCartItems ? <AddonCart addonCartItems={addonCartItems} isCartView onCheckout={onCheckout} onGoToCart={onGoToCart} paymentOptions={addonsPaymentOptions} hideSeeCartButton={true} /> : <S.EmptyCart>
                      <img src={EmptyCart} alt="empty-cart" />
                      <h3>Seu carrinho esta vazio!</h3>
                      <p>Adicione itens para proceder ao checkout</p>
                    </S.EmptyCart>}
                </S.ContainerCart>
              </ClickAwayListener>
            </Paper>
          </Fade>}
      </Popper>
    </>;
};
export default ShoppingCartDropdown;